import { useState } from "react";
import { AlertTriangle, Megaphone, RefreshCw, Save, Send, ShieldCheck, type LucideIcon } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  saveMailChannel, testMailChannel,
  type ChannelMode, type MailChannelId, type MailChannelView, type MailChannelsResponse,
} from "../../../../lib/mail";
import { Btn, StatusPill, type StatusTone } from "../../../ui";
import { IntegrationCard } from "../panel/IntegrationCard";
import { Field, Note, Pill, SaveRow, SecretPill, SelectPill, ToggleRow } from "../panel/fields";
import { MailChannelWebhook } from "./MailChannelWebhook";
import { smtpSecureForPort, smtpTlsHint } from "./smtpPort";

interface Props {
  id: MailChannelId;
  channel: MailChannelView;
  allowedModes: ChannelMode[];
  onSaved: (next: MailChannelsResponse) => void;
}

/** The two identities are different jobs, not two copies of one: the receipts a
 *  client must receive, and the campaign a client may unsubscribe from. */
const CHANNEL_ICON: Record<MailChannelId, LucideIcon> = {
  system: ShieldCheck,
  marketing: Megaphone,
};

/**
 * How the head's pill reads the server's own resolution.
 *
 * `platform` is `wt` rather than `ok`: mail does go out, but on the platform's
 * shared account, so a reputation problem on it is a reputation problem here.
 * That is worth a different colour from an identity of our own.
 */
const RESOLUTION_TONE: Record<MailChannelView["resolution"], StatusTone> = {
  own: "ok",
  platform: "wt",
  unavailable: "lt",
};

/**
 * One sending channel. The pill reports the SERVER's resolution rather than
 * whether the fields look filled in - a channel can have every box completed and
 * still send nothing (no from-address, switched off, a mode it is not allowed to
 * use), and that gap is exactly where a campaign silently goes nowhere.
 */
export function MailChannelPanel({ id, channel, allowedModes, onSaved }: Props) {
  const t = useT();
  const [enabled, setEnabled] = useState(channel.enabled);
  const [mode, setMode] = useState<ChannelMode>(channel.mode);
  const [fromAddress, setFromAddress] = useState(channel.fromAddress);
  const [fromName, setFromName] = useState(channel.fromName);
  const [replyTo, setReplyTo] = useState(channel.replyTo);
  const [resendApiKey, setResendApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [smtp, setSmtp] = useState({ ...channel.smtp, pass: "" });
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [testTo, setTestTo] = useState("");
  const [testState, setTestState] = useState<"idle" | "sending">("idle");
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaveState("saving");
    try {
      const next = await saveMailChannel(id, {
        enabled, mode, fromAddress, fromName, replyTo,
        resendApiKey: resendApiKey || undefined,
        webhookSecret: webhookSecret || undefined,
        smtp: { host: smtp.host, port: smtp.port, secure: smtp.secure, user: smtp.user, pass: smtp.pass || undefined },
      });
      setResendApiKey("");
      setWebhookSecret("");
      setSmtp((s) => ({ ...s, pass: "" }));
      onSaved(next);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  async function handleTest() {
    if (!testTo) return;
    setTestState("sending");
    setTestResult(null);
    try {
      const r = await testMailChannel(id, testTo);
      setTestResult({
        ok: r.ok,
        text: r.ok ? t("settings.channels.testSent") : (r.error || t(`settings.channels.status.${r.status}`)),
      });
    } catch {
      setTestResult({ ok: false, text: t("settings.channels.testFailed") });
    } finally {
      setTestState("idle");
    }
  }

  const reasonKey = channel.reason ? `settings.channels.reason.${channel.reason}` : "";

  return (
    <IntegrationCard
      icon={CHANNEL_ICON[id]}
      title={t(`settings.channels.${id}.title`)}
      desc={t(`settings.channels.${id}.desc`)}
      status={
        <StatusPill tone={RESOLUTION_TONE[channel.resolution]}>
          {t(`settings.channels.resolution.${channel.resolution}`)}
        </StatusPill>
      }
    >
      <form onSubmit={handleSave} className="space-y-5">
        {channel.resolution === "unavailable" ? (
          <Note tone="warn" icon={<AlertTriangle className="mt-0.5 size-4 shrink-0" />}>
            {t("settings.channels.nothingSent")} {reasonKey ? t(reasonKey) : null}
          </Note>
        ) : null}
        {channel.resolution === "own" && channel.effectiveFrom ? (
          <Note tone="mut">
            {t("settings.channels.effectiveFrom")}{" "}
            <span className="font-mono">{channel.effectiveFrom}</span>
          </Note>
        ) : null}
        {channel.sharesPlatformAccount ? (
          <Note tone="bad" icon={<AlertTriangle className="mt-0.5 size-4 shrink-0" />}>
            {t("settings.channels.sharesPlatform")}
          </Note>
        ) : null}

        <ToggleRow label={t(`settings.channels.${id}.enabled`)} checked={enabled} onChange={setEnabled} />

        <Field
          label={t("settings.channels.modeLabel")}
          hint={t(`settings.channels.modeHint.${mode}`)}
          className="sm:max-w-[320px]"
        >
          <SelectPill value={mode} onChange={(v) => setMode(v as ChannelMode)}>
            {allowedModes.map((m) => (
              <option key={m} value={m}>
                {t(`settings.channels.mode.${m}`)}
              </option>
            ))}
          </SelectPill>
        </Field>

        {mode !== "platform" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t("settings.channels.fromAddress")}
              hint={mode === "mailbox" ? t("settings.channels.fromInherit") : undefined}
            >
              <Pill
                value={fromAddress}
                onChange={setFromAddress}
                type="email"
                placeholder="info@webadmin.ee"
                autoComplete="off"
              />
            </Field>
            <Field label={t("settings.channels.fromName")}>
              <Pill value={fromName} onChange={setFromName} placeholder="WebAdmin" autoComplete="off" />
            </Field>
            <Field label={t("settings.channels.replyTo")} hint={t("settings.channels.replyToHint")}>
              <Pill value={replyTo} onChange={setReplyTo} placeholder="myyk@webadmin.ee" autoComplete="off" />
            </Field>
          </div>
        ) : null}

        {mode === "resend" ? (
          <Field
            label={t("settings.mail.resendApiKey")}
            hint={channel.resendApiKeySet && !resendApiKey ? t("settings.mail.passwordKept") : undefined}
            className="sm:max-w-[420px]"
          >
            <SecretPill
              value={resendApiKey}
              onChange={setResendApiKey}
              placeholder={channel.resendApiKeySet ? "••••••••" : "re_..."}
            />
          </Field>
        ) : null}

        {mode === "smtp" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("settings.mail.smtpHost")}>
              <Pill
                value={smtp.host}
                onChange={(v) => setSmtp({ ...smtp, host: v })}
                placeholder="smtp.zone.eu"
                autoComplete="off"
              />
            </Field>
            <div className="grid min-w-0 grid-cols-2 gap-4">
              <Field label={t("settings.mail.smtpPort")}>
                <Pill
                  value={smtp.port}
                  onChange={(v) => {
                    // Same rule as the mailbox card: the well-known ports decide
                    // the TLS mode, so the toggle follows the port.
                    const port = Number(v);
                    setSmtp({ ...smtp, port, secure: smtpSecureForPort(port, smtp.secure) });
                  }}
                  type="number"
                  inputMode="numeric"
                />
              </Field>
              <div className="flex min-w-0 items-center sm:pt-7">
                <ToggleRow
                  label={t("settings.mail.smtpSecure")}
                  hint={smtpTlsHint(t, smtp.port, smtp.secure)}
                  checked={smtp.secure}
                  onChange={(v) => setSmtp({ ...smtp, secure: v })}
                />
              </div>
            </div>
            <Field label={t("settings.mail.smtpUser")}>
              <Pill value={smtp.user} onChange={(v) => setSmtp({ ...smtp, user: v })} autoComplete="off" />
            </Field>
            <Field
              label={t("settings.mail.smtpPass")}
              hint={channel.smtp.passSet && !smtp.pass ? t("settings.mail.passwordKept") : undefined}
            >
              <SecretPill
                value={smtp.pass}
                onChange={(v) => setSmtp({ ...smtp, pass: v })}
                placeholder={channel.smtp.passSet ? "••••••••" : ""}
              />
            </Field>
          </div>
        ) : null}

        {/* Below the credentials, because it is the return path for the account
            they configure. On the platform mode there is no account of ours at
            the provider, so there is nothing to point a webhook at. */}
        {mode !== "platform" ? (
          <MailChannelWebhook channel={channel} secret={webhookSecret} onSecret={setWebhookSecret} />
        ) : null}

        <SaveRow>
          <Btn type="submit" tone="dark" small disabled={saveState === "saving"}>
            {saveState === "saving" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            {saveState === "saving" ? t("settings.mail.saving") : t("settings.mail.save")}
          </Btn>
          {saveState === "saved" ? <StatusPill tone="ok">{t("settings.mail.saved")}</StatusPill> : null}
          {saveState === "error" ? <StatusPill tone="lt">{t("settings.mail.saveError")}</StatusPill> : null}
        </SaveRow>
      </form>

      {/* The test send goes through this channel and nothing else, so a green
          result is proof that this account and its DNS work. */}
      <div className="mt-5 space-y-3 border-t border-[var(--crm-hair)] pt-5">
        <div className="flex flex-wrap items-end gap-2.5">
          {/* The button stays outside the field's label: it acts on the channel,
              not on the address box, and only the box is what the label names. */}
          <Field label={t("settings.channels.test")} className="flex-1 sm:max-w-[340px]">
            <Pill
              value={testTo}
              onChange={setTestTo}
              type="email"
              placeholder={t("common.example.yourEmail")}
              autoComplete="off"
            />
          </Field>
          <Btn small onClick={handleTest} disabled={testState === "sending" || !testTo}>
            {testState === "sending" ? (
              <RefreshCw className="size-3.5 animate-spin" />
            ) : (
              <Send className="size-3.5" />
            )}
            {t("settings.channels.testSend")}
          </Btn>
        </div>
        <p className="crm-sub !mt-0 text-[11.5px] leading-snug">{t("settings.channels.testHint")}</p>
        {testResult ? (
          <Note tone={testResult.ok ? "ok" : "bad"} live>
            {testResult.text}
          </Note>
        ) : null}
      </div>
    </IntegrationCard>
  );
}
